// Placeholder for registerTenant to resolve no-undef
const registerTenant = () => {};
/**
 * Tenant Configuration System
 * Provides tenant-specific theming and branding based on query params or headers
 */

// Domain allowlist for returnUrl security validation
const ALLOWED_RETURN_DOMAINS = {
  // MagicFurnish-owned properties (pattern-based)
  magicfurnishPattern: 'magicfurnish.com',

  // External partner domains (explicit allowlist)
  partnerDomains: [
    'serhant.magicfurnish.com',
    // Future partner domains can be added here
  ]
};

/**
 * Validates if a returnUrl is safe to redirect to
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if URL is allowed, false otherwise
 */
export const validateReturnUrl = (url) => {
  if (!url) return false;

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;

    // Check MagicFurnish subdomain pattern (*.magicfurnish.com)
    if (hostname === ALLOWED_RETURN_DOMAINS.magicfurnishPattern ||
      hostname.endsWith(`.${ALLOWED_RETURN_DOMAINS.magicfurnishPattern}`)) {
      return true;
    }

    // Check explicit partner domain allowlist
    if (ALLOWED_RETURN_DOMAINS.partnerDomains.includes(hostname)) {
      return true;
    }

    return false;
  } catch (error) {
    console.error('Invalid returnUrl format:', error);
    return false;
  }
};

// Tenant configuration registry
const TENANT_CONFIGS = {
  serhant: {
    name: 'Serhant',
    /**
     * Canonical brand wordmark for UI copy (all-caps + trailing period, per
     * SERHANT. brand guidelines). Optional/additive — callers fall back to
     * `name` when absent, so existing tenants are unaffected.
     */
    brandDisplayName: 'SERHANT.',
    primaryColor: '#061F84',
    backLinkText: 'Back to S.MPLE',
    hostApp: 'smple',
    logo: '/SERHANT_Logo.jpg',
    /**
     * Uploaded once to /public and applied to every new user's branding
     * on signup via applyTenantDefaultLogo(). Backend owns the canonical
     * file after that — this is the seed asset only.
     */
    defaultLogoAsset: '/serhant-default-logo.png',
    domains: [
      'serhant.magicfurnish.com',
      'staging-serhant.magicfurnish.com',
      'serhant-dev.magicfurnish.com',
    ],
    /** Partner-agent emails for analytics audience segregation (Agent vs Lead). */
    agentEmailDomain: '@serhant.com',
    autoApproveUser: true,  // Serhant allows self-serve account creation → "Create Account"
    /**
     * Serhant is a guest-upload partner ("No account needed"): guests can upload
     * a floor plan before signing in. Seeded here so first-paint guest routing
     * (`/` and `/main`) is correct WITHOUT waiting on the /partners/domain
     * fetch. The backend remains the source of truth and reconciles this value
     * in the background on every boot.
     */
    allowGuestUpload: true
  },
};

// Default MagicFurnish configuration
const DEFAULT_CONFIG = {
  name: 'MagicFurnish',
  primaryColor: '#1B1B1B',
  backLinkText: 'Back',
  hostApp: null,
  logo: null,
  domains: [],
  autoApproveUser: false,  // Default requires manual approval → "Request Account"
  allowGuestUpload: false  // Default: guests must authenticate before uploading
};

/**
 * Extracts tenant information from URL query params or headers
 * @returns {string|null} - Tenant ID or null if not found
 */
/**
 * Partner-agent email domain for analytics (e.g. "@serhant.com") from a
 * partner hostname such as "staging-serhant.magicfurnish.com".
 *
 * @param {string} hostname
 * @returns {string|null}
 */
export const getPartnerAgentEmailDomain = (hostname = '') => {
  const host = String(hostname || '').trim().toLowerCase();
  if (!host || host === 'all') return null;

  for (const config of Object.values(TENANT_CONFIGS)) {
    if (
      config.agentEmailDomain &&
      Array.isArray(config.domains) &&
      config.domains.some((d) => String(d).toLowerCase() === host)
    ) {
      return config.agentEmailDomain;
    }
  }
  return null;
};

/** All configured partner-agent email domains (for all-partners admin view). */
export const getConfiguredPartnerAgentEmailDomains = () => {
  const domains = new Set();
  for (const config of Object.values(TENANT_CONFIGS)) {
    if (config.agentEmailDomain) domains.add(config.agentEmailDomain);
  }
  return [...domains];
};

export const getTenantFromRequest = () => {
  try {
    // Check URL query parameters first
    const params = new URLSearchParams(window.location.search);
    const partnerParam = params.get('partner');

    if (partnerParam && TENANT_CONFIGS[partnerParam]) {
      return partnerParam;
    }


    const partnerHeader = document.querySelector('meta[name="partner"]')?.content;
    if (partnerHeader && TENANT_CONFIGS[partnerHeader]) {
      return partnerHeader;
    }

    // Fallback: Check domain-based tenant detection (legacy support)
    const hostname = window.location.hostname;
    for (const [tenantId, config] of Object.entries(TENANT_CONFIGS)) {
      if (config.domains.includes(hostname)) {
        return tenantId;
      }
    }

    return null;
  } catch (error) {
    console.error('Error detecting tenant:', error);
    return null;
  }
};

/**
 * Gets the complete tenant configuration
 * @param {string|null} tenantId - Optional tenant ID, will auto-detect if not provided
 * @returns {Object} - Tenant configuration object
 */
export const getTenantConfig = (tenantId = null) => {
  const tenant = tenantId || getTenantFromRequest();

  if (tenant && TENANT_CONFIGS[tenant]) {
    return {
      ...TENANT_CONFIGS[tenant],
      tenantId: tenant,
      isDefault: false
    };
  }

  return {
    ...DEFAULT_CONFIG,
    tenantId: null,
    isDefault: true
  };
};

/**
 * Gets tenant-specific branding color
 * @param {string|null} tenantId - Optional tenant ID
 * @returns {string} - Hex color code
 */
export const getTenantBrandingColor = (tenantId = null) => {
  const config = getTenantConfig(tenantId);
  return config.primaryColor;
};

/**
 * Gets tenant-specific back link text
 * @param {string|null} tenantId - Optional tenant ID
 * @returns {string} - Back link text
 */
export const getTenantBackLinkText = (tenantId = null) => {
  const config = getTenantConfig(tenantId);
  return config.backLinkText;
};

/**
 * Gets tenant-specific autoApproveUser setting
 * @param {string|null} tenantId - Optional tenant ID
 * @returns {boolean} - Whether user accounts are auto-approved (true="Create Account", false="Request Account")
 */
export const getTenantAutoApproveUser = (tenantId = null) => {
  const config = getTenantConfig(tenantId);
  return config.autoApproveUser;
};

/**
 * Gets the tenant-specific guest-upload default. Used to seed the guest-upload
 * routing flag synchronously on first paint (before the /partners/domain fetch),
 * so partner guests are routed correctly without a boot-time spinner.
 * @param {string|null} tenantId - Optional tenant ID
 * @returns {boolean} - Whether guests may upload without authenticating
 */
export const getTenantAllowGuestUpload = (tenantId = null) => {
  const config = getTenantConfig(tenantId);
  return Boolean(config.allowGuestUpload);
};

/**
 * Gets the return URL for the webview exit with security validation
 * @returns {string|null} - Validated return URL or null if invalid/missing
 */
export const getReturnUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const returnUrl = params.get('returnUrl');

    if (returnUrl) {
      const decodedUrl = decodeURIComponent(returnUrl);

      // Validate the URL against our security allowlist
      if (validateReturnUrl(decodedUrl)) {
        return decodedUrl;
      } else {
        console.warn('returnUrl rejected by security validation:', decodedUrl);
        return null;
      }
    }

    return null;
  } catch (error) {
    console.error('Error parsing return URL:', error);
    return null;
  }
};

const tenantConfig = {
  getTenantConfig,
  getTenantBrandingColor,
  getTenantBackLinkText,
  getTenantAutoApproveUser,
  getTenantAllowGuestUpload,
  getReturnUrl,
  getTenantFromRequest,
  registerTenant,
  validateReturnUrl,
};
export default tenantConfig;